import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AppError, type AdminContentArticle, type ContentArticleInput } from '@st/contract';
import type {
  MeasurementGuideAdmin,
  MeasurementGuideInput,
  MeasurementStepKey,
  TransformationInput,
  TransformationPatch,
} from '@st/contract';
import type {
  ApiClient,
  AssignProgramInput,
  CalendarItem,
  CheckInRow,
  ConversationRow,
  ExerciseInput,
  GoalChoice,
  GoalRow,
  HabitRow,
  MeasurementGuide,
  MessageRow,
  NotificationPreferenceRow,
  NotificationRow,
  NotificationType,
  ProductOutput,
  ProgramInput,
  SeriesMetric,
  SeriesOutput,
  SeriesRange,
  TrialStatus,
  WeeklyClientRow,
  WorkoutInput,
} from '@st/contract';
import type {
  AchievementRow,
  BusinessMetrics,
  ContentCategory,
  ContentDetail,
  ContentListItem,
  DiscountCodeRow,
  DiscountValidation,
  WeeklySummaryRow,
  AdminPriceInput,
  AdminPriceEdit,
  StripeSyncPreview,
} from '@st/contract';
import { useApi, useAuth } from '../auth/AuthProvider';
import { toExerciseInput, withWorkoutExercises, withWorkoutPatch } from '../lib/program';
import type {
  AdminPaymentRow,
  AdminProductInput,
  AdminProductRow,
  AdminSubmissionRow,
  AdminUserRow,
  DashboardOutput,
  ExerciseRow,
  ListOutput,
  MeOutput,
  MeUpdateInput,
  MealInput,
  MealTemplateInput,
  MealTemplateRow,
  MeasurementOutput,
  MealFeedbackInput,
  NutritionDayOutput,
  NutritionHistoryOutput,
  OnboardingQuestion,
  OnboardingStateOutput,
  PhotoPose,
  ProgramRow,
  ProgramTree,
  ProgressPhotoOutput,
  SubmissionStatus,
  SubscriptionOutput,
  TrainerClient,
  TrainerClientDetail,
  TodayOutput,
  WeekPlanOutput,
  WorkoutExerciseRow,
  WorkoutDetail,
  WorkoutLogResult,
} from './types';

/**
 * Ključevi upita na jednom mjestu — invalidacija nikad ne pogađa krivi podskup.
 */
export const qk = {
  me: ['me'] as const,
  dashboard: ['me', 'dashboard'] as const,
  plan: ['training', 'plan'] as const,
  workout: (id: string) => ['training', 'workout', id] as const,
  nutritionDay: (day: string) => ['nutrition', 'day', day] as const,
  nutritionHistory: ['nutrition', 'history'] as const,
  measurements: ['progress', 'measurements'] as const,
  photos: ['progress', 'photos'] as const,
  subscription: ['billing', 'subscription'] as const,
  onboarding: ['onboarding', 'questions'] as const,
  onboardingState: ['onboarding', 'state'] as const,
  goalChoices: ['onboarding', 'goal-choices'] as const,
  measurementGuide: ['onboarding', 'measurement-guide'] as const,
  today: ['training', 'today'] as const,
  trialStatus: ['training', 'trial-status'] as const,
  publicProducts: (locale: string) => ['public', 'products', locale] as const,
  trainerClients: ['trainer', 'clients'] as const,
  trainerClient: (id: string) => ['trainer', 'client', id] as const,
  trainerPrograms: ['trainer', 'programs'] as const,
  trainerProgram: (id: string) => ['trainer', 'program', id] as const,
  trainerExercises: ['trainer', 'exercises'] as const,
  adminUsers: ['admin', 'users'] as const,
  adminProducts: ['admin', 'products'] as const,
  stripeSyncPreview: ['admin', 'stripe', 'sync-preview'] as const,
  adminPayments: ['admin', 'payments'] as const,
  adminContent: ['admin', 'content'] as const,
  adminSubmissions: ['admin', 'submissions'] as const,

  // --- faza 2 ---
  conversations: ['messages', 'conversations'] as const,
  chatMessages: (conversationId: string) => ['messages', 'thread', conversationId] as const,
  notifications: (filter: NotificationFilter) => ['notifications', 'list', filter] as const,
  notificationsUnread: ['notifications', 'unread-count'] as const,
  notificationPrefs: ['notifications', 'preferences'] as const,
  webPushKey: ['notifications', 'web-push-key'] as const,
  trainerMealTemplates: ['trainer', 'meal-templates'] as const,
  mealTemplates: ['nutrition', 'templates'] as const,
  measurementGuideAdmin: ['trainer', 'measurement-guide'] as const,
  habits: (days: number) => ['habits', days] as const,
  goals: ['goals'] as const,
  checkInCurrent: ['checkins', 'current'] as const,
  checkInHistory: ['checkins', 'history'] as const,
  checkInsPending: ['checkins', 'pending'] as const,
  calendar: (from: string, to: string, clientId?: string) =>
    ['calendar', from, to, clientId ?? 'me'] as const,
  series: (metric: SeriesMetric, range: SeriesRange, clientId?: string) =>
    ['progress', 'series', metric, range, clientId ?? 'me'] as const,
  trainerWeekly: ['trainer', 'weekly'] as const,

  // --- faza 3 ---
  weeklySummaries: ['summaries', 'weekly'] as const,
  achievements: ['achievements'] as const,
  /** Isti podaci kao gore, ali za tuđi id — trenerov pogled na svog klijenta. */
  trainerClientSummaries: (id: string) => ['trainer', 'client', id, 'summaries'] as const,
  trainerClientAchievements: (id: string) => ['trainer', 'client', id, 'achievements'] as const,
  trainerClientNutrition: (id: string, day: string) =>
    ['trainer', 'client', id, 'nutrition', day] as const,
  trainerClientNutritionDays: (id: string) =>
    ['trainer', 'client', id, 'nutrition', 'days'] as const,
  content: (category: ContentCategory | 'all') => ['content', 'list', category] as const,
  contentArticle: (slug: string) => ['content', 'article', slug] as const,
  discountCodes: ['admin', 'discount-codes'] as const,
  businessMetrics: ['admin', 'metrics'] as const,
};

export type NotificationFilter = 'all' | 'unread';

/** Poruka koja još nije potvrđena sa servera nosi ovaj prefiks u id-u. */
export const OPTIMISTIC_PREFIX = 'optimistic:';

export const isOptimistic = (id: string): boolean => id.startsWith(OPTIMISTIC_PREFIX);

/** Kad socket ne radi, ekran razgovora prelazi na povlačenje ovim ritmom. */
export const CHAT_POLL_MS = 15_000;

/**
 * `raw` je tipizirani prolaz kroz isti `createClient` — nema ručno pisanog fetcha.
 * Ostaje samo za rute koje `@st/contract` još nema kao imenovanu metodu; sve
 * ostalo ide kroz klijenta, pa URL i oblik odgovora žive na jednom mjestu.
 */
const get = <T,>(api: ApiClient, path: string): Promise<T> => api.raw<T>('GET', path);

export function useMe(): UseQueryResult<MeOutput> {
  const api = useApi();
  return useQuery({ queryKey: qk.me, queryFn: () => api.me.get() });
}

export function useDashboard(): UseQueryResult<DashboardOutput> {
  const api = useApi();
  return useQuery({ queryKey: qk.dashboard, queryFn: () => api.me.dashboard() });
}

/** `enabled` je za ekrane koje dijele klijent i trener — trener nema svoj plan. */
export function useTrainingPlan(enabled = true): UseQueryResult<WeekPlanOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.plan,
    queryFn: () => api.training.plan(),
    enabled,
  });
}

export function useWorkout(id: string): UseQueryResult<WorkoutDetail> {
  const api = useApi();
  return useQuery({
    queryKey: qk.workout(id),
    queryFn: () => api.training.workout(id),
  });
}

export function useNutritionDay(day: string): UseQueryResult<NutritionDayOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.nutritionDay(day),
    queryFn: () => api.nutrition.day(day),
  });
}

/** Dnevnik unatrag, sažet po danu — ulaz u ranije dane bez pogađanja datuma. */
export function useNutritionHistory(): UseQueryResult<NutritionHistoryOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.nutritionHistory,
    queryFn: () => api.nutrition.history(),
  });
}

/**
 * Upis obroka.
 *
 * `clientIdempotencyKey` nastaje u ekranu pri OTVARANJU obrasca, ne ovdje: isti
 * ključ mora preživjeti ponovni pokušaj nakon pale mreže, inače bi drugi klik
 * na „Spremi" stvorio drugi obrok (pravilo 10).
 */
export function useAddMeal() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MealInput) => api.nutrition.addMeal(input),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: qk.nutritionDay(input.eatenOn) });
      void qc.invalidateQueries({ queryKey: qk.nutritionHistory });
      // Početna crta kalorije za danas — bez ovoga ostaje stara brojka.
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

/**
 * Fotografija obroka — isti tok kao fotografija napretka: dozvola → bajtovi u
 * R2 → potvrda. Zapis nastaje TEK pri potvrdi, pa prekinut prijenos ne ostavlja
 * redak koji pokazuje na nepostojeći objekt.
 *
 * Razlika je jedna i bitna: obrok mora postojati PRIJE fotografije, jer ruta za
 * dozvolu ide na `/v1/nutrition/meals/:id/photo-url`. Zato ovo nije dio
 * `useAddMeal` nego zaseban korak — ako fotografija padne, obrok je već spremljen
 * i ne gubi se. Ekran zbog toga nudi ponovni pokušaj samo za sliku.
 */
export interface MealPhotoUpload {
  mealId: string;
  day: string;
  file: File;
  /** Postavlja se samo pri ponovnom pokušaju nakon pale POTVRDE. */
  uploadedKey?: string | null;
  onStage?: (stage: PhotoUploadStage) => void;
  onUploaded?: (key: string) => void;
}

export function useUploadMealPhoto() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MealPhotoUpload): Promise<{ photoId: string; key: string }> => {
      let key = input.uploadedKey ?? null;

      if (!key) {
        input.onStage?.('grant');
        const grant = await api.nutrition.mealPhotoUrl(input.mealId, {
          contentType: input.file.type,
          bytes: input.file.size,
        });

        input.onStage?.('upload');
        await api.files.upload(grant.uploadUrl, input.file);
        key = grant.key;
        input.onUploaded?.(key);
      }

      input.onStage?.('confirm');
      const result = await api.nutrition.confirmMealPhoto(input.mealId, { key });
      return { photoId: result.photoId, key };
    },
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: qk.nutritionDay(input.day) });
    },
  });
}

/** Brisanje obroka. `day` služi samo za invalidaciju pravog dana u cacheu. */
export function useRemoveMeal() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; day: string }) => api.nutrition.removeMeal(id),
    onSuccess: (_result, { day }) => {
      void qc.invalidateQueries({ queryKey: qk.nutritionDay(day) });
      void qc.invalidateQueries({ queryKey: qk.nutritionHistory });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
  });
}

export function useMeasurements(): UseQueryResult<ListOutput<MeasurementOutput>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.measurements,
    queryFn: () => get<ListOutput<MeasurementOutput>>(api, '/v1/progress/measurements'),
  });
}

export function usePhotos(): UseQueryResult<ListOutput<ProgressPhotoOutput>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.photos,
    queryFn: () => api.progress.photos(),
  });
}

/** Korak prijenosa fotografije; ekran po njemu javlja dokle je stiglo. */
export type PhotoUploadStage = 'grant' | 'upload' | 'confirm';

export interface ProgressPhotoUpload {
  file: File;
  takenOn: string;
  pose: PhotoPose;
  note?: string;
  /**
   * Ključ datoteke koja je već legla u R2. Postavlja se samo pri ponovnom
   * pokušaju nakon pale POTVRDE — tada se bajtovi ne šalju drugi put.
   */
  uploadedKey?: string | null;
  onStage?: (stage: PhotoUploadStage) => void;
  /**
   * Javlja se čim bajtovi legnu u R2, PRIJE potvrde. Ekran ključ pamti, pa
   * ponovni pokušaj kreće od potvrde umjesto od prijenosa cijele datoteke.
   */
  onUploaded?: (key: string) => void;
}

/**
 * Prijenos fotografije napretka u tri koraka:
 * dozvola → bajtovi direktno u R2 → potvrda.
 *
 * Zapis u bazi nastaje TEK pri potvrdi (vidi komentar u `apps/api/.../progress.ts`):
 * da prijenos padne na pola, u popisu ne ostaje redak s URL-om koji vodi u 404.
 * Zato je i ponovni pokušaj siguran — server ponovljenu potvrdu istog ključa
 * prepozna i vrati postojeći zapis umjesto duplikata.
 */
export function useUploadProgressPhoto() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProgressPhotoUpload): Promise<{ photoId: string; key: string }> => {
      let key = input.uploadedKey ?? null;

      if (!key) {
        input.onStage?.('grant');
        const grant = await api.progress.photoUploadUrl({
          contentType: input.file.type,
          bytes: input.file.size,
        });

        input.onStage?.('upload');
        await api.files.upload(grant.uploadUrl, input.file);
        key = grant.key;
        input.onUploaded?.(key);
      }

      input.onStage?.('confirm');
      const result = await api.progress.confirmPhoto({
        key,
        takenOn: input.takenOn,
        pose: input.pose,
        ...(input.note ? { note: input.note } : {}),
      });
      return { photoId: result.photoId, key };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.photos }),
  });
}

export function useSubscription(): UseQueryResult<SubscriptionOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.subscription,
    queryFn: () => api.billing.subscription(),
  });
}

export function useOnboardingQuestions(): UseQueryResult<ListOutput<OnboardingQuestion>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.onboarding,
    queryFn: () => get<ListOutput<OnboardingQuestion>>(api, '/v1/onboarding/questions'),
  });
}

// ─── čarobnjak nakon registracije ───────────────────────────────────────────

/**
 * Zapamćeni korak čarobnjaka. Trener je tražio da se upitnik nastavi ondje gdje
 * je stao, a ne da kreće ispočetka. `apps/api` korak već upisuje, ali GET rute
 * još nema — dok je ne dobije, 404 znači „kreni od nule”, a ne greška ekrana.
 */
export function useOnboardingState(): UseQueryResult<OnboardingStateOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.onboardingState,
    queryFn: async () => {
      try {
        return await api.onboarding.state();
      } catch (err) {
        if (err instanceof AppError && err.code === 'not_found') {
          return {
            currentStep: 0,
            completedAt: null,
            basicsDone: false,
            goalDone: false,
            programChosen: false,
            basics: null,
            goal: null,
          };
        }
        throw err;
      }
    },
  });
}

export function useGoalChoices(): UseQueryResult<ListOutput<GoalChoice>> {
  const api = useApi();
  return useQuery({ queryKey: qk.goalChoices, queryFn: () => api.onboarding.goalChoices() });
}

/** Uputa za mjerenje: video ako ga trener snimi, inače pisani koraci. */
export function useMeasurementGuide(enabled = true): UseQueryResult<MeasurementGuide> {
  const api = useApi();
  return useQuery({
    queryKey: qk.measurementGuide,
    queryFn: () => api.onboarding.measurementGuide(),
    enabled,
  });
}

export function usePublicProducts(locale: 'hr' | 'en'): UseQueryResult<ListOutput<ProductOutput>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.publicProducts(locale),
    queryFn: () => api.publicCatalog.products(locale),
  });
}

function useOnboardingInvalidator() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.onboardingState });
    void qc.invalidateQueries({ queryKey: qk.me });
  };
}

export function useSubmitBasics() {
  const api = useApi();
  const invalidate = useOnboardingInvalidator();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['onboarding']['basics']>[0]) =>
      api.onboarding.basics(input),
    onSuccess: invalidate,
  });
}

export function useSubmitGoal() {
  const api = useApi();
  const invalidate = useOnboardingInvalidator();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['onboarding']['goal']>[0]) => api.onboarding.goal(input),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** Odabir programa bez plaćanja — otključava prvih N treninga. */
export function useChooseProgram() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productSlug: string) => api.trial.chooseProgram(productSlug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trialStatus });
      void qc.invalidateQueries({ queryKey: qk.plan });
      void qc.invalidateQueries({ queryKey: qk.today });
    },
  });
}

/** Završetak čarobnjaka — jedini poziv koji zaključava `onboarding` gate. */
export function useFinishOnboarding() {
  const api = useApi();
  const invalidate = useOnboardingInvalidator();
  return useMutation({
    mutationFn: (step: number) => api.onboarding.answers({ answers: {}, completed: true, step }),
    onSuccess: invalidate,
  });
}

// ─── probni pristup i zaključani treninzi ───────────────────────────────────

export function useTrialStatus(): UseQueryResult<TrialStatus> {
  const api = useApi();
  return useQuery({ queryKey: qk.trialStatus, queryFn: () => api.trial.status() });
}

/**
 * Trening dana. `reason: 'locked'` NIJE greška — ekran tada nudi kupnju,
 * a ne prazno stanje (zahtjev trenera, sekcija 2).
 */
export function useToday(): UseQueryResult<TodayOutput> {
  const api = useApi();
  return useQuery({ queryKey: qk.today, queryFn: () => api.training.today() });
}

export function useCheckout() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['billing']['checkout']>[0]) =>
      api.billing.checkout(input),
  });
}

export function useTrainerClients(): UseQueryResult<ListOutput<TrainerClient>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClients,
    queryFn: () => get<ListOutput<TrainerClient>>(api, '/v1/trainer/clients'),
  });
}

export function useTrainerClient(id: string): UseQueryResult<TrainerClientDetail> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClient(id),
    queryFn: () => get<TrainerClientDetail>(api, `/v1/trainer/clients/${id}`),
  });
}

export function useTrainerPrograms(): UseQueryResult<ListOutput<ProgramRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.trainerPrograms, queryFn: () => api.trainerPrograms.list() });
}

/** Cijelo stablo programa u jednom zahtjevu — builder ne radi n+1 poziva. */
export function useProgramTree(id: string): UseQueryResult<ProgramTree> {
  const api = useApi();
  return useQuery({ queryKey: qk.trainerProgram(id), queryFn: () => api.trainerPrograms.tree(id) });
}

export function useTrainerExercises(): UseQueryResult<ListOutput<ExerciseRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.trainerExercises, queryFn: () => api.trainerPrograms.exercises() });
}

export function useAdminUsers(): UseQueryResult<ListOutput<AdminUserRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.adminUsers,
    queryFn: () => get<ListOutput<AdminUserRow>>(api, '/v1/admin/users'),
  });
}

export function useAdminProducts(): UseQueryResult<ListOutput<AdminProductRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.adminProducts, queryFn: () => api.admin.products() });
}

export function useAdminPayments(): UseQueryResult<ListOutput<AdminPaymentRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.adminPayments, queryFn: () => api.admin.payments() });
}

export function useAdminSubmissions(): UseQueryResult<ListOutput<AdminSubmissionRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.adminSubmissions, queryFn: () => api.admin.submissions() });
}

export function useAdminContent(): UseQueryResult<{ items: AdminContentArticle[] }> {
  const api = useApi();
  return useQuery({ queryKey: qk.adminContent, queryFn: () => api.adminContent.list() });
}

/** Pisanje edukacijskih članaka. Svaka mutacija osvježava isti popis. */
export function useAdminContentMutations() {
  const api = useApi();
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.adminContent });

  return {
    create: useMutation({
      mutationFn: (b: ContentArticleInput) => api.adminContent.create(b),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (vars: { id: string; body: Partial<ContentArticleInput> }) =>
        api.adminContent.update(vars.id, vars.body),
      onSuccess: invalidate,
    }),
    setPublished: useMutation({
      mutationFn: (vars: { id: string; published: boolean }) =>
        vars.published ? api.adminContent.publish(vars.id) : api.adminContent.unpublish(vars.id),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.adminContent.remove(id),
      onSuccess: invalidate,
    }),
  };
}

// --- mutacije ---

/**
 * `PATCH /v1/me` vraća `{ ok: true }`, ne novi profil — zato se cache ne piše
 * rukom nego se upit poništi i profil dohvati ponovno. Prije je ovdje stajao
 * `setQueryData` s odgovorom rute, pa bi ekran nakon spremanja pokazao `{ok}`
 * umjesto profila čim se portal spoji na pravi API.
 */
export function useUpdateMe() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: MeUpdateInput) => api.me.update(patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export function useAddMeasurement() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['progress']['addMeasurement']>[0]) =>
      api.progress.addMeasurement(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.measurements }),
  });
}

/**
 * Završetak treninga. Odgovor nosi i novootključana postignuća (`achievements`
 * u `workoutCompletedOutput`) — server ih šalje samo prvi put, pa ih ekran smije
 * pokazati bez dodatne provjere. Oblik dolazi iz ugovora, bez castanja.
 */
export function useLogWorkout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Parameters<ApiClient['training']['logWorkout']>[0],
    ): Promise<WorkoutLogResult> => api.training.logWorkout(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.dashboard });
      void qc.invalidateQueries({ queryKey: qk.plan });
      void qc.invalidateQueries({ queryKey: qk.achievements });
      void qc.invalidateQueries({ queryKey: qk.weeklySummaries });
    },
  });
}

export function useCustomerPortal() {
  const api = useApi();
  return useMutation({ mutationFn: () => api.billing.customerPortal() });
}

// --- program builder ---

/**
 * Optimistično ide samo ono što je sigurno vratiti unatrag: redoslijed vježbi
 * i preimenovanja. Strukturne promjene (dupliciranje, brisanje, novi entiteti)
 * server tek izdaje s novim id-evima, pa one idu kroz `invalidateQueries`.
 */
function useTreeInvalidator(programId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.trainerProgram(programId) });
    void qc.invalidateQueries({ queryKey: qk.trainerPrograms });
  };
}

export function useCreateProgram() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramInput) => api.trainerPrograms.create(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerPrograms }),
  });
}

export function useDeleteProgram() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.trainerPrograms.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerPrograms }),
  });
}

/** Preimenovanje je sigurna operacija — piše se odmah u cache pa tek onda na server. */
export function useUpdateProgram(programId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<ProgramInput>) => api.trainerPrograms.update(programId, patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.trainerProgram(programId) });
      const previous = qc.getQueryData<ProgramTree>(qk.trainerProgram(programId));
      if (previous) {
        qc.setQueryData<ProgramTree>(qk.trainerProgram(programId), {
          ...previous,
          program: { ...previous.program, ...patch },
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.trainerProgram(programId), ctx.previous);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerPrograms }),
  });
}

export function useAddWeek(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (label?: string) =>
      api.trainerPrograms.addWeek(programId, label ? { labelHr: label } : {}),
    onSuccess: invalidate,
  });
}

export function useDuplicateWeek(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (weekId: string) => api.trainerPrograms.duplicateWeek(weekId),
    onSuccess: invalidate,
  });
}

export function useDeleteWeek(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (weekId: string) => api.trainerPrograms.deleteWeek(weekId),
    onSuccess: invalidate,
  });
}

export function useAddWorkout(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (v: { weekId: string; input: WorkoutInput }) =>
      api.trainerPrograms.addWorkout(v.weekId, v.input),
    onSuccess: invalidate,
  });
}

export function useDuplicateWorkout(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (workoutId: string) => api.trainerPrograms.duplicateWorkout(workoutId),
    onSuccess: invalidate,
  });
}

export function useDeleteWorkout(programId: string) {
  const api = useApi();
  const invalidate = useTreeInvalidator(programId);
  return useMutation({
    mutationFn: (workoutId: string) => api.trainerPrograms.deleteWorkout(workoutId),
    onSuccess: invalidate,
  });
}

export function useUpdateWorkout(programId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { workoutId: string; patch: Partial<WorkoutInput> }) =>
      api.trainerPrograms.updateWorkout(v.workoutId, v.patch),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.trainerProgram(programId) });
      const previous = qc.getQueryData<ProgramTree>(qk.trainerProgram(programId));
      if (previous) {
        qc.setQueryData(qk.trainerProgram(programId), withWorkoutPatch(previous, v.workoutId, v.patch));
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.trainerProgram(programId), ctx.previous);
    },
  });
}

export interface SetExercisesVars {
  workoutId: string;
  /** CIJELA lista, redoslijedom. Server briše staru i upisuje ovu. */
  rows: WorkoutExerciseRow[];
  /** Redoslijed je siguran za optimistični upis; dodavanje i uređivanje nije. */
  optimistic?: boolean;
}

/**
 * Jedini put kojim vježbe ulaze u trening. Nikad se ne šalje pojedinačna pozicija —
 * `setWorkoutExercisesInput` traži cijelu listu pa dvije vježbe ne mogu dijeliti sortOrder.
 */
export function useSetWorkoutExercises(
  programId: string,
): UseMutationResult<{ ok: true; count: number }, unknown, SetExercisesVars, { previous?: ProgramTree }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: SetExercisesVars) =>
      api.trainerPrograms.setExercises(v.workoutId, { exercises: v.rows.map(toExerciseInput) }),
    onMutate: async (v) => {
      if (!v.optimistic) return {};
      await qc.cancelQueries({ queryKey: qk.trainerProgram(programId) });
      const previous = qc.getQueryData<ProgramTree>(qk.trainerProgram(programId));
      if (previous) {
        qc.setQueryData(qk.trainerProgram(programId), withWorkoutExercises(previous, v.workoutId, v.rows));
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.trainerProgram(programId), ctx.previous);
    },
    onSuccess: (_d, v) => {
      // Nove vježbe dobivaju id na serveru — samo redoslijed smije ostati na optimističnom stanju.
      if (!v.optimistic) void qc.invalidateQueries({ queryKey: qk.trainerProgram(programId) });
    },
  });
}

export function useAssignProgram(programId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignProgramInput) => api.trainerPrograms.assign(programId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trainerClients });
      void qc.invalidateQueries({ queryKey: qk.trainerPrograms });
    },
  });
}

/**
 * Trenerovi predlošci obroka — jelovnik koji slaže jednom, a klijenti biraju.
 */
export function useTrainerMealTemplates(): UseQueryResult<ListOutput<MealTemplateRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerMealTemplates,
    queryFn: () => api.trainerPrograms.mealTemplates(),
  });
}

export function useSaveMealTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; input: MealTemplateInput }): Promise<{ id: string }> => {
      if (v.id) {
        await api.trainerPrograms.updateMealTemplate(v.id, v.input);
        return { id: v.id };
      }
      return api.trainerPrograms.createMealTemplate(v.input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerMealTemplates }),
  });
}

export function useDeleteMealTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.trainerPrograms.deleteMealTemplate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerMealTemplates }),
  });
}

/** Klijentova strana istog jelovnika: predlošci njegovog trenera. */
export function useMealTemplates(): UseQueryResult<ListOutput<MealTemplateRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.mealTemplates, queryFn: () => api.nutrition.templates() });
}

/**
 * Uputa „kako se mjeri" kakvu vidi onaj tko je uređuje.
 *
 * Odvojeno od `useMeasurementGuide` koju gleda klijent: ondje su samo potpisane
 * adrese snimki, ovdje i ključevi objekata. Bez ključa spremanje bi upisalo
 * potpisanu adresu koja istekne za sat vremena, pa bi snimka „nestala".
 */
export function useTrainerMeasurementGuide(): UseQueryResult<MeasurementGuideAdmin> {
  const api = useApi();
  return useQuery({
    queryKey: qk.measurementGuideAdmin,
    queryFn: () => api.trainerPrograms.measurementGuide(),
  });
}

export function useSaveMeasurementGuide() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeasurementGuideInput) => api.trainerPrograms.saveMeasurementGuide(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.measurementGuideAdmin });
      // Ekran klijenta čita istu uputu s druge rute.
      void qc.invalidateQueries({ queryKey: qk.measurementGuide });
    },
  });
}

/**
 * Prijenos snimke za JEDNU točku mjerenja.
 *
 * Vraća samo ključ; spremanje upute je zaseban korak, pa prekinut prijenos ne
 * ostavlja uputu koja pokazuje na nepostojeći objekt.
 */
export function useUploadMeasurementVideo() {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: {
      stepKey: MeasurementStepKey;
      file: File;
    }): Promise<{ key: string }> => {
      const grant = await api.trainerPrograms.measurementVideoUrl({
        stepKey: input.stepKey,
        contentType: input.file.type === 'video/webm' ? 'video/webm' : 'video/mp4',
      });
      await api.files.upload(grant.uploadUrl, input.file);
      return { key: grant.key };
    },
  });
}

export function useSaveExercise() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; input: ExerciseInput }): Promise<{ id: string }> => {
      if (v.id) {
        await api.trainerPrograms.updateExercise(v.id, v.input);
        return { id: v.id };
      }
      return api.trainerPrograms.createExercise(v.input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.trainerExercises }),
  });
}

// --- admin piše ---

/**
 * Ručno odobrenje pristupa.
 *
 * Postoji jer Stripe nije jedini put do uplate: dio ljudi plaća na račun ili na
 * ruke. Bilješka je obavezna — ručno otvoren pristup je odluka o novcu koju
 * netko mora moći objasniti kasnije.
 */
export function useGrantAccess() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; productId: string; activeUntil?: string | null; note: string }) =>
      api.admin.grantAccess(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useRevokeAccess() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { userId: string; note: string }) => api.admin.revokeAccess(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminUsers }),
  });
}

export function useSaveProduct() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; input: AdminProductInput }): Promise<{ id: string }> => {
      if (v.id) {
        await api.admin.updateProduct(v.id, v.input);
        return { id: v.id };
      }
      return api.admin.createProduct(v.input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

/**
 * Nova cijena ili izmjena postojeće.
 *
 * Iznos se NE mijenja ovim putem: Stripe cijene su nepromjenjive, pa bi izmjena
 * značila da cjenik pokazuje jedan iznos a kartica skida drugi. Za promjenu
 * iznosa postoji `useReplacePrice`, koji radi novu cijenu i gasi staru.
 */
export function useSavePrice() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      v:
        | { mode: 'create'; productId: string; input: AdminPriceInput }
        | { mode: 'edit'; priceId: string; input: AdminPriceEdit },
    ): Promise<{ id: string }> => {
      if (v.mode === 'edit') {
        await api.admin.updatePrice(v.priceId, v.input);
        return { id: v.priceId };
      }
      return api.admin.createPrice(v.productId, v.input);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

/** Promjena iznosa: nova cijena, stara ugašena. */
export function useReplacePrice() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { priceId: string; input: AdminPriceInput }) =>
      api.admin.replacePrice(v.priceId, v.input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminProducts }),
  });
}

export function useStripeSyncPreview(): UseQueryResult<StripeSyncPreview> {
  const api = useApi();
  return useQuery({ queryKey: qk.stripeSyncPreview, queryFn: () => api.admin.stripeSyncPreview() });
}

export function useStripeSync() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.admin.stripeSync(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminProducts });
      void qc.invalidateQueries({ queryKey: qk.stripeSyncPreview });
    },
  });
}

export function useRefundPayment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { paymentId: string; reason?: string }) =>
      api.admin.refund(v.paymentId, v.reason ? { reason: v.reason } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminPayments }),
  });
}

export function useUpdateSubmission() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; status: SubmissionStatus }) =>
      api.admin.updateSubmission(v.id, { status: v.status }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.adminSubmissions });
      const previous = qc.getQueryData<ListOutput<AdminSubmissionRow>>(qk.adminSubmissions);
      if (previous) {
        qc.setQueryData<ListOutput<AdminSubmissionRow>>(qk.adminSubmissions, {
          items: previous.items.map((s) => (s.id === v.id ? { ...s, status: v.status } : s)),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.adminSubmissions, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.adminSubmissions }),
  });
}

// ─── faza 2: poruke ─────────────────────────────────────────────────────────

export function useConversations(): UseQueryResult<ListOutput<ConversationRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.conversations, queryFn: () => api.messages.conversations() });
}

/**
 * Povijest razgovora. `poll` uključuje povlačenje SAMO kad socket ne radi —
 * dok veza stoji, nove poruke stižu kroz `useConversationSocket` i upisuju se
 * u ovaj isti cache bez ijednog dodatnog zahtjeva.
 */
export function useChatMessages(
  conversationId: string | null,
  poll = false,
): UseQueryResult<ListOutput<MessageRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.chatMessages(conversationId ?? 'none'),
    queryFn: () => api.messages.history(conversationId!),
    enabled: conversationId !== null,
    refetchInterval: poll ? CHAT_POLL_MS : false,
  });
}

export function useOpenConversation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partnerId?: string) => api.messages.open(partnerId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.conversations }),
  });
}

export interface SendChatVars {
  body: string;
  /** Idempotencija: ponovni pokušaj iste poruke ne stvara duplikat u bazi. */
  clientIdempotencyKey: string;
}

/**
 * Slanje ide REST-om, nikad socketom — poruka mora biti u bazi prije nego
 * postane vidljiva bilo kome. Optimistična poruka drži mjesto do odgovora,
 * pa je zamijeni pravi id sa servera.
 */
export function useSendChatMessage(conversationId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (vars: SendChatVars) =>
      api.messages.send(conversationId, {
        body: vars.body,
        clientIdempotencyKey: vars.clientIdempotencyKey,
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: qk.chatMessages(conversationId) });
      const previous = qc.getQueryData<ListOutput<MessageRow>>(qk.chatMessages(conversationId));
      const tempId = `${OPTIMISTIC_PREFIX}${vars.clientIdempotencyKey}`;
      const optimistic: MessageRow = {
        id: tempId,
        senderId: user?.id ?? '',
        mine: true,
        body: vars.body,
        attachmentUrl: null,
        readAt: null,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<ListOutput<MessageRow>>(qk.chatMessages(conversationId), (old) => ({
        items: [...(old?.items ?? []), optimistic],
      }));
      return { previous, tempId };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.chatMessages(conversationId), ctx.previous);
    },
    onSuccess: (data, _v, ctx) => {
      qc.setQueryData<ListOutput<MessageRow>>(qk.chatMessages(conversationId), (old) =>
        old
          ? {
              items: old.items.map((m) =>
                m.id === ctx.tempId
                  ? { ...m, id: data.id, createdAt: data.createdAt ?? m.createdAt }
                  : m,
              ),
            }
          : old,
      );
      qc.setQueryData<ListOutput<ConversationRow>>(qk.conversations, (old) =>
        old
          ? {
              items: old.items.map((c) =>
                c.id === conversationId
                  ? { ...c, lastMessageAt: data.createdAt ?? new Date().toISOString() }
                  : c,
              ),
            }
          : old,
      );
    },
  });
}

/** Oznaka pročitano pri otvaranju razgovora — brojač nepročitanih pada na nulu. */
export function useMarkConversationRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.messages.markRead(conversationId),
    onSuccess: (_d, conversationId) => {
      qc.setQueryData<ListOutput<ConversationRow>>(qk.conversations, (old) =>
        old
          ? { items: old.items.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c)) }
          : old,
      );
      void qc.invalidateQueries({ queryKey: qk.notificationsUnread });
    },
  });
}

// ─── faza 2: obavijesti ─────────────────────────────────────────────────────

/**
 * Badge čita SAMO brojač. Prethodni projekt je za zvonce dohvaćao cijelu listu
 * od 80 zapisa; ova ruta vraća jedan broj.
 */
export function useUnreadNotificationCount(): UseQueryResult<{ count: number }> {
  const api = useApi();
  return useQuery({
    queryKey: qk.notificationsUnread,
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: 60_000,
  });
}

/** Lista se dohvaća tek kad je popis otvoren — `enabled` je namjerno uvjet. */
export function useNotifications(
  filter: NotificationFilter,
  enabled: boolean,
): UseQueryResult<ListOutput<NotificationRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.notifications(filter),
    queryFn: () => api.notifications.list(filter),
    enabled,
  });
}

/**
 * Označavanje je po zapisu i može se poništiti. Otvaranje popisa NIKAD ne
 * poziva ovu mutaciju — to je izričito bio bug u prethodnom projektu.
 */
export function useMarkNotification() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; read: boolean }) =>
      vars.read ? api.notifications.markRead(vars.id) : api.notifications.markUnread(vars.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useNotificationPreferences(): UseQueryResult<
  ListOutput<NotificationPreferenceRow>
> {
  const api = useApi();
  return useQuery({ queryKey: qk.notificationPrefs, queryFn: () => api.notifications.preferences() });
}

/**
 * Javni VAPID ključ. Mijenja se samo deployem, pa nema razloga da se dohvaća
 * iznova pri svakom otvaranju profila.
 */
export function useWebPushKey(): UseQueryResult<{ publicKey: string | null }> {
  const api = useApi();
  return useQuery({
    queryKey: qk.webPushKey,
    queryFn: () => api.notifications.webPushKey(),
    staleTime: Infinity,
  });
}

export function useSetNotificationPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { type: NotificationType; push?: boolean; email?: boolean }) =>
      api.notifications.setPreference(vars),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: qk.notificationPrefs });
      const previous = qc.getQueryData<ListOutput<NotificationPreferenceRow>>(qk.notificationPrefs);
      if (previous) {
        qc.setQueryData<ListOutput<NotificationPreferenceRow>>(qk.notificationPrefs, {
          items: previous.items.map((p) =>
            p.type === vars.type
              ? { ...p, push: vars.push ?? p.push, email: vars.email ?? p.email }
              : p,
          ),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.notificationPrefs, ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.notificationPrefs }),
  });
}

// ─── faza 2: navike i ciljevi ───────────────────────────────────────────────

export const HABIT_WINDOW_DAYS = 30;

export function useHabits(
  days = HABIT_WINDOW_DAYS,
): UseQueryResult<{ items: HabitRow[]; from: string; to: string }> {
  const api = useApi();
  return useQuery({ queryKey: qk.habits(days), queryFn: () => api.habits.list(days) });
}

export function useCreateHabit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['habits']['create']>[0]) => api.habits.create(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['habits'] }),
  });
}

export function useDeleteHabit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.habits.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['habits'] }),
  });
}

/**
 * Označavanje dana je idempotentno po (navika, dan) pa je optimistični upis
 * siguran: ponovni klik samo prepiše isti zapis.
 */
export function useLogHabit(days = HABIT_WINDOW_DAYS) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; loggedOn: string; done: boolean }) =>
      api.habits.log(vars.id, { loggedOn: vars.loggedOn, done: vars.done }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: qk.habits(days) });
      const previous = qc.getQueryData<{ items: HabitRow[]; from: string; to: string }>(
        qk.habits(days),
      );
      if (previous) {
        qc.setQueryData(qk.habits(days), {
          ...previous,
          items: previous.items.map((h) =>
            h.id === vars.id
              ? {
                  ...h,
                  doneToday: vars.loggedOn === previous.to ? vars.done : h.doneToday,
                  history: vars.done
                    ? [...h.history.filter((x) => x.day !== vars.loggedOn), { day: vars.loggedOn, value: null }]
                    : h.history.filter((x) => x.day !== vars.loggedOn),
                }
              : h,
          ),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(qk.habits(days), ctx.previous);
    },
    // Niz i postotak računa server — nakon upisa se stanje mora poravnati.
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.habits(days) }),
  });
}

export function useGoals(): UseQueryResult<ListOutput<GoalRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.goals, queryFn: () => api.goals.list() });
}

export function useCreateGoal() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.goals.create(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.goals }),
  });
}

export function useUpdateGoal() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      api.goals.update(vars.id, vars.patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.goals }),
  });
}

// ─── faza 2: check-in ───────────────────────────────────────────────────────

export function useCurrentCheckIn(enabled = true): UseQueryResult<CheckInRow> {
  const api = useApi();
  return useQuery({ queryKey: qk.checkInCurrent, queryFn: () => api.checkIns.current(), enabled });
}

export function useCheckInHistory(): UseQueryResult<ListOutput<CheckInRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.checkInHistory, queryFn: () => api.checkIns.history() });
}

export function useSubmitCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; values: Record<string, unknown> }) =>
      api.checkIns.submit(vars.id, vars.values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checkins'] });
      void qc.invalidateQueries({ queryKey: qk.measurements });
    },
  });
}

export function usePendingCheckIns(): UseQueryResult<ListOutput<CheckInRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.checkInsPending, queryFn: () => api.checkIns.pending() });
}

export function useReviewCheckIn() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; response: string }) =>
      api.checkIns.review(vars.id, vars.response),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checkins'] });
      void qc.invalidateQueries({ queryKey: qk.trainerWeekly });
    },
  });
}

// ─── faza 2: kalendar ───────────────────────────────────────────────────────

export function useCalendar(
  from: string,
  to: string,
  clientId?: string,
  /** Trener bez odabranog klijenta nema što dohvatiti — inače bi dobio svoj prazan kalendar. */
  enabled = true,
): UseQueryResult<{ from: string; to: string; items: CalendarItem[] }> {
  const api = useApi();
  return useQuery({
    queryKey: qk.calendar(from, to, clientId),
    queryFn: () => api.calendar.range({ from, to, ...(clientId ? { clientId } : {}) }),
    enabled,
  });
}

export function useCreateCalendarEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.calendar.createEvent(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

/** Trener mijenja popis sudionika grupne aktivnosti; šalje se željeno stanje. */
export function useSetParticipants() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; clientIds: string[] }) =>
      api.calendar.setParticipants(input.groupId, input.clientIds),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

/**
 * Klijent si sam upiše kad će trenirati. Podsjetnik je zadano sat vremena prije
 * (`remindMinutesBefore` se zato NE šalje kad je 60 — server već ima tu vrijednost
 * kao zadanu, a `null` bi ga ugasio).
 */
export function usePlanWorkout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<ApiClient['calendar']['planWorkout']>[0]) =>
      api.calendar.planWorkout(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

/** Premještanje termina; server pritom vraća podsjetnik u red. */
export function useUpdateCalendarEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      patch: Parameters<ApiClient['calendar']['updateEvent']>[1];
    }) => api.calendar.updateEvent(vars.id, vars.patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

export function useDeleteCalendarEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.calendar.removeEvent(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['calendar'] }),
  });
}

// ─── faza 2: grafovi i tjedni pregled ───────────────────────────────────────

/**
 * Sažetak (promjena, min, max, prosjek) dolazi sa servera i NIKAD se ne računa
 * ovdje — inače bi se dva klijenta razišla na istim podacima.
 */
export function useSeries(
  metric: SeriesMetric,
  range: SeriesRange,
  clientId?: string,
): UseQueryResult<SeriesOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.series(metric, range, clientId),
    queryFn: () => api.series.get(metric, range, clientId),
  });
}

export function useTrainerWeekly(): UseQueryResult<{
  weekStart: string | null;
  items: WeeklyClientRow[];
}> {
  const api = useApi();
  return useQuery({ queryKey: qk.trainerWeekly, queryFn: () => api.trainerWeekly() });
}


// ─── faza 3: sažetak, postignuća, edukacija, popusti, metrike ───────────────

/** Tjedni sažetci, najnoviji prvi. Tjedan bez ijednog traga server ne generira. */
export function useWeeklySummaries(): UseQueryResult<ListOutput<WeeklySummaryRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.weeklySummaries, queryFn: () => api.summaries.weekly() });
}

/**
 * Samo otključana postignuća — server namjerno ne šalje zaključana, pa portal
 * nikad ne popisuje ono što netko nema.
 */
export function useAchievements(): UseQueryResult<ListOutput<AchievementRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.achievements, queryFn: () => api.achievements.list() });
}

/**
 * Sažetci klijenta koje trener vodi. Isti oblik kao `useWeeklySummaries`, samo
 * za tuđi id; server prije odgovora provjerava aktivnu vezu, pa 403 ovdje znači
 * „nije tvoj klijent" i ekran ga prikazuje kao grešku, ne kao prazno stanje.
 */
export function useTrainerClientSummaries(
  clientId: string,
): UseQueryResult<ListOutput<WeeklySummaryRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClientSummaries(clientId),
    queryFn: () => api.trainerClients.summaries(clientId),
  });
}

/** Postignuća klijenta koje trener vodi; natpisi stižu na trenerovom jeziku. */
export function useTrainerClientAchievements(
  clientId: string,
): UseQueryResult<ListOutput<AchievementRow>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClientAchievements(clientId),
    queryFn: () => api.trainerClients.achievements(clientId),
  });
}

/**
 * Prehrana klijenta iz trenerova kuta.
 *
 * Oblik je isti kao klijentov `useNutritionDay` jer ga server računa istim
 * kodom — trener i klijent ne smiju vidjeti različit zbroj kalorija.
 */
export function useTrainerClientNutrition(
  clientId: string,
  day: string,
): UseQueryResult<NutritionDayOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClientNutrition(clientId, day),
    queryFn: () => api.trainerClients.nutrition(clientId, day),
  });
}

/** Dani koji uopće imaju upisan obrok — bez toga trener lista prazne datume. */
export function useTrainerClientNutritionDays(
  clientId: string,
): UseQueryResult<NutritionHistoryOutput> {
  const api = useApi();
  return useQuery({
    queryKey: qk.trainerClientNutritionDays(clientId),
    queryFn: () => api.trainerClients.nutritionHistory(clientId),
  });
}

/**
 * Komentar ili palac uz obrok.
 *
 * Šalje se samo polje koje se mijenja: palac ne smije obrisati raniji komentar,
 * ni komentar ugasiti palac. `clientId` i `day` služe samo osvježavanju popisa.
 */
export function useMealFeedback(clientId: string, day: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mealId: string } & MealFeedbackInput) =>
      api.trainerClients.mealFeedback(input.mealId, {
        ...(input.comment !== undefined ? { comment: input.comment } : {}),
        ...(input.liked !== undefined ? { liked: input.liked } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trainerClientNutrition(clientId, day) });
    },
  });
}

export function useContentList(
  category?: ContentCategory,
): UseQueryResult<ListOutput<ContentListItem>> {
  const api = useApi();
  return useQuery({
    queryKey: qk.content(category ?? 'all'),
    queryFn: () => api.content.list(category),
  });
}

/**
 * Članak. 402 nije greška nego stanje pristupa, pa se ne pokušava ponovno —
 * ekran na taj kod prikazuje poziv na kupnju.
 */
export function useContentArticle(slug: string): UseQueryResult<ContentDetail> {
  const api = useApi();
  return useQuery({
    queryKey: qk.contentArticle(slug),
    queryFn: () => api.content.read(slug),
    retry: (count, error) =>
      error instanceof AppError && error.code === 'payment_required' ? false : count < 1,
  });
}

/** Provjera koda prije plaćanja. Odbijanje je legitiman odgovor, ne greška. */
export function useValidateDiscount(): UseMutationResult<
  DiscountValidation,
  unknown,
  { code: string; priceId?: string }
> {
  const api = useApi();
  return useMutation({
    mutationFn: (vars) => api.discounts.validate(vars.code, vars.priceId),
  });
}

export function useDiscountCodes(): UseQueryResult<ListOutput<DiscountCodeRow>> {
  const api = useApi();
  return useQuery({ queryKey: qk.discountCodes, queryFn: () => api.discounts.list() });
}

export function useCreateDiscountCode() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.discounts.create(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.discountCodes }),
  });
}

export function useSetDiscountActive() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; active: boolean }) =>
      api.discounts.setActive(vars.id, vars.active),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.discountCodes }),
  });
}

/** Poslovne metrike se računaju na serveru — admin i izvještaji vide iste brojke. */
export function useBusinessMetrics(): UseQueryResult<BusinessMetrics> {
  const api = useApi();
  return useQuery({ queryKey: qk.businessMetrics, queryFn: () => api.metrics() });
}

/* ──────────────────────────────────────────────────────────────────────────
   Transformacije „prije / poslije" za javni sajt
   ────────────────────────────────────────────────────────────────────────── */

export const transformacijeKey = ['trainer', 'transformations'] as const;

export function useTransformacije() {
  const api = useApi();
  return useQuery({
    queryKey: transformacijeKey,
    queryFn: () => api.trainerPrograms.transformations(),
  });
}

export function useSpremiTransformaciju() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id?: string; input: TransformationInput | TransformationPatch }) =>
      v.id
        ? api.trainerPrograms.updateTransformation(v.id, v.input as TransformationPatch)
        : api.trainerPrograms.createTransformation(v.input as TransformationInput),
    onSuccess: () => void qc.invalidateQueries({ queryKey: transformacijeKey }),
  });
}

export function useObjaviTransformaciju() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; objavi: boolean }) =>
      v.objavi
        ? api.trainerPrograms.publishTransformation(v.id)
        : api.trainerPrograms.unpublishTransformation(v.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: transformacijeKey }),
  });
}

export function useObrisiTransformaciju() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.trainerPrograms.removeTransformation(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: transformacijeKey }),
  });
}

/**
 * Prijenos fotografije ide u dva koraka: potpisana dozvola, pa bajtovi ravno u
 * R2. Ključ se sprema tek kad prijenos prođe — polovična fotografija ne smije
 * ostati zapisana kao da je tu.
 */
export function usePosaljiFotografiju() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; faza: 'prije' | 'poslije'; file: File }) => {
      const tip =
        v.file.type === 'image/png'
          ? 'image/png'
          : v.file.type === 'image/webp'
            ? 'image/webp'
            : 'image/jpeg';
      const grant = await api.trainerPrograms.transformationPhotoUrl(v.id, {
        faza: v.faza,
        contentType: tip,
      });
      await api.files.upload(grant.uploadUrl, v.file);
      return api.trainerPrograms.updateTransformation(v.id, {
        [v.faza === 'prije' ? 'photoBeforeKey' : 'photoAfterKey']: grant.key,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: transformacijeKey }),
  });
}
